import { useEffect, useRef } from 'react';

/**
 * Detecta lecturas de una pistola de código de barras.
 *
 * Una pistola actúa como un teclado: "tipea" los dígitos del código muy rápido
 * (unos pocos ms entre tecla y tecla) y termina con Enter. Un humano tipea mucho
 * más lento, así que distinguimos el escaneo por la VELOCIDAD entre teclas.
 *
 * - Si las teclas llegan rápido (< maxInterKeyMs) y terminan en Enter → es un escaneo.
 * - Si llegan lento (tipeo manual) → el buffer se reinicia y no dispara nada.
 */
interface UseBarcodeScannerOptions {
  onScan: (code: string) => void;
  /** Largo mínimo para considerarlo un código válido (evita falsos positivos). */
  minLength?: number;
  /** Máximo de ms entre teclas para considerarlo "ráfaga" de pistola. */
  maxInterKeyMs?: number;
  /** Permite apagar el escáner (ej: cuando la pantalla no está activa). */
  enabled?: boolean;
}

export function useBarcodeScanner({
  onScan,
  minLength = 3,
  maxInterKeyMs = 35,
  enabled = true,
}: UseBarcodeScannerOptions) {
  // Guardamos onScan en un ref para no re-suscribir el listener en cada render.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const bufferRef = useRef('');
  const lastTimeRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      // Ignoramos combinaciones (Ctrl/Alt/Meta): no son parte de un escaneo.
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const now = Date.now();
      const gap = now - lastTimeRef.current;
      lastTimeRef.current = now;

      // Si pasó demasiado tiempo desde la última tecla, arrancamos un buffer nuevo
      // (esto descarta el tipeo humano, que es lento).
      if (gap > maxInterKeyMs) {
        bufferRef.current = '';
      }

      if (e.key === 'Enter') {
        const code = bufferRef.current.trim();
        bufferRef.current = '';
        if (code.length >= minLength) {
          e.preventDefault();
          onScanRef.current(code);
        }
        return;
      }

      // Solo caracteres imprimibles simples (dígitos/letras del código).
      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, minLength, maxInterKeyMs]);
}
